<script setup lang="ts">
import { useFormValidation } from "../../../../composables/useFormValidation";
import { Amount, required } from "../../../../utils/validation";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import AmountInput from "../../../../components/ui/form/AmountInput.vue";
import { PaymentType } from "../../../../types/models";
import { onMounted, ref, watch } from "vue";
import {
  downloadPDF,
  formatNumber,
  printPDF,
  sharePDF,
} from "../../../../utils/CommonFunctions";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import MessageBox from "../../../../services/MessageBox";
import TextInput from "../../../../components/ui/form/TextInput.vue";
import FileUpload from "../../../../components/ui/form/FileUpload.vue";
import DocumentHeader from "../../../../components/ui/Templates/DocumentHeader.vue";
import DocumentFooter from "../../../../components/ui/Templates/DocumentFooter.vue";
import ModalFormComponent from "../../../../components/ui/common/ModalFormComponent.vue";

const props = withDefaults(
  defineProps<{
    data?: any | null;
    mode?: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
    title?: string | null;
    endPoint?: string;
    permissions?: Permission[];
    listCreateName?: string;
  }>(),
  {
    title: "Receive Payment",
    endPoint: "/clients/finance/collections/payment-receipts/",
    mode: "create",
  }
);
const emit = defineEmits<{
  (e: "save"): void;
  (e: "close"): void;
}>();
const isReadOnly = ref<boolean>(props.mode === "view");

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  setFormData,
  resetForm,
} = useFormValidation({
  date_time: props.data?.date_time || new Date().toISOString().split("T")[0],
  customers_id: props.data?.customers_id || "",
  customer: props.data?.customer || "",
  document_no: props.data?.document_no || "",
  sales_invoices_id: props.data?.id || "",
  payment_types_id: props.data?.payment_types_id || "",
  currencys_id: props.data?.currencys_id || 1,
  exchange_rate: props.data?.exchange_rate || 1,
  net_total: props.data?.net_total || 0,
  due_amount: props.data?.due_amount || 0,
  payment_amount: props.data?.payment_amount || 0,
  chart_of_accounts_id: props.data?.chart_of_accounts_id || "",
  attachment: props.data?.attachment || "",
  reference: props.data?.reference || "",
  action: "",
  invoices: [] as {
    uid: any;
    net_total: any;
    paid_amount: number;
    payment_amount: any;
  }[],
});

setFieldRules("customers_id", [required("Customer is required")]);
setFieldRules("invoices", [required("at least 1 Invoice is required")]);
setFieldRules("payment_types_id", [required("Payment Type is is required")]);
setFieldRules("payment_amount", [
  required("Payment Amount is required"),
  Amount("Payment should be more than 0"),
]);

const payment_type = ref<PaymentType>();

const handleSubmit = async (
  _new_record: boolean,
  download: boolean = false,
  mail: boolean = false,
  share: boolean = false,
  print = false
) => {
  if (
    payment_type.value?.allow_over_tender == false &&
    formData.value.payment_amount > formData.value.due_amount
  ) {
    MessageBox.show("Payment amount cannot be more than Due amount", "warning");
    return;
  }
  if (validateForm()) {
    formData.value.invoices = [
      {
        uid: props.data?.uid,
        net_total: formData.value.net_total,
        paid_amount: formData.value.net_total - formData.value.due_amount,
        payment_amount: formData.value.payment_amount,
      },
    ];
    if (!formData.value.invoices || formData.value.invoices.length <= 0) {
      MessageBox.show("Select at least 1 Invoice to proceed", "warning");
      return;
    }
    formData.value.action =
      download || share || print ? "download" : mail ? "mail" : "";
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + "create",
      formData.value
    );
    if (response.success == true) {
      isReadOnly.value = true;
      Toast.show(props.title + " Saved successfully");
      emit("save");
      if (download) {
        downloadPDF(response.data.file);
      } else if (mail) {
        Toast.show(props.title + " mailed successfully");
      } else if (share) {
        sharePDF(
          response.data.file,
          props.title + " from Faseyha POS",
          "viber"
        );
      } else if (print) {
        printPDF(response.data.file);
      }
      emit("save");
      emit("close");
    }
  }
};

const handlePaymentType = (data?: PaymentType) => {
  payment_type.value = data;
  if (data?.require_account == true) {
    setFieldRules("chart_of_accounts_id", [
      required("Chart of Account is required"),
    ]);
  } else {
    setFieldRules("chart_of_accounts_id", []);
  }
  if (data?.require_attachment == true) {
    setFieldRules("attachment", [required("Attachment is required")]);
  } else {
    setFieldRules("attachment", []);
  }
  if (data?.require_reference == true) {
    setFieldRules("reference", [required("Rerference is required")]);
  } else {
    setFieldRules("reference", []);
  }
  if (data?.chart_of_accounts_id)
    fields.value.chart_of_accounts_id.value = data?.chart_of_accounts_id;
};

onMounted(() => {
  console.log(props.data);
  if (props.data) {
    //setFormData(props.data)
  }
});
watch(
  () => props.data,
  (newVal) => {
    if (newVal) {
      //setFormData(props.data)
    }
  }
);

const showForm = ref(false);
onMounted(() => {
  showForm.value = true;
  isReadOnly.value = props.mode == "view";
  if (props.mode != "create") {
    setFormData(props.data);
  } else {
    resetForm();
  }
});

const hanldeOcrRead = (reference: string, amount: string) => {
  if (reference) fields.value.reference.value = reference;
  if (amount) {
    fields.value.payment_amount.value = amount;
  }
};
</script>

<template>
  <ModalFormComponent width="55rem" :fullscreen="false" :header="false" @close="$emit('close')">
    <template #header>
      <DocumentHeader
        :title="title"
        :end-point="endPoint"
        :data="formData"
        :readonly="isReadOnly"
        :show-quick-view="false"  
        :show-drop-down="false"
        @save="handleSubmit(false)"
        @save-new="handleSubmit(true)"
        @download="handleSubmit(false, true)"
        @mail="handleSubmit(false, false, true)"
        @share="handleSubmit(false, false, false, true)"
        @print="handleSubmit(false, false, false, false, true)"
        @close="$emit('close')"
      />
    </template>

    <template #form> 
    <!-- header end-->
    <!-- body start -->
    <div class="flex-1 overflow-y-auto">
      <div class="p-6 bg-white dark:bg-gray-800">
        <div class="grid md:grid-cols-2 gap-6 mb-8">
          <TextInput
            label="Customer"
            v-model="formData.customer"
            :readonly="true"
          />
          <TextInput
            label="Invoice No"
            v-model="formData.document_no"
            :readonly="true"
          />
        </div>
        <div class="grid grid-cols-2 gap-6 mb-8">
          <div class="text-left">
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >Invoice Amount</label
            >
            <div class="mt-1">
              <input
                type="text"
                disabled
                class="appearance-none text-right block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors border-gray-300 dark:border-gray-600"
                :value="formatNumber(formData.net_total)"
              />
            </div>
          </div>
          <div class="text-left">
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >Due Amount</label
            >
            <div class="mt-1">
              <input
                type="text"
                disabled
                class="appearance-none text-right block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors border-gray-300 dark:border-gray-600"
                :value="formatNumber(formData.due_amount)"
              />
            </div>
          </div>
        </div>
        <div class="grid md:grid-cols-2 gap-6 mb-8">
          <div class="grid grid-cols-3 gap-6">
            <SearchSelect
              label="Currency"
              end-point="finance/master-data/currency"
              v-model="fields.currencys_id.value"
              :readonly="isReadOnly"
              class="col-span-2"
              :withDetails="true"
              :error="
                fields.currencys_id.touched ? fields.currencys_id.error : null
              "
              @blur="fields.currencys_id.touched = true"
            />
            <AmountInput
              v-model="fields.exchange_rate.value"
              :readonly="true"
              label="Ex. Rate"
              :error="
                fields.exchange_rate.touched ? fields.exchange_rate.error : null
              "
              @blur="fields.exchange_rate.touched = true"
            />
          </div>
          <AmountInput
            v-model="fields.payment_amount.value"
            label="Payment Amount"
            :error="
              fields.payment_amount.touched ? fields.payment_amount.error : null
            "
            @blur="fields.payment_amount.touched = true"
          />
        </div>
        <div class="grid md:grid-cols-2 gap-6 mb-8">
          <SearchSelect
            label="Payment Type"
            end-point="finance/master-data/payment-types"
            v-model="fields.payment_types_id.value"
            :readonly="isReadOnly"
            remove="credit"
            :withDetails="true"
            :error="
              fields.payment_types_id.touched
                ? fields.payment_types_id.error
                : null
            "
            @blur="fields.payment_types_id.touched = true"
            @select="handlePaymentType"
          />

          <SearchSelect
            label="Bank/Cash Account"
            end-point="finance/accounting/chart-of-accounts"
            v-model="fields.chart_of_accounts_id.value"
            extra="Bank,Cash"
            :readonly="isReadOnly"
            :withDetails="true"
            :error="
              fields.chart_of_accounts_id.touched
                ? fields.chart_of_accounts_id.error
                : null
            "
            @blur="fields.chart_of_accounts_id.touched = true"
          />
        </div>
        <div>
          <FileUpload
            v-model="fields.attachment.value"
            v-if="payment_type && payment_type?.require_attachment"
            label="Attachment"
            path="payment_receipts"
            :readonly="isReadOnly"
            :ocr="payment_type.read_ocr"
            :ocr-length="payment_type.ocr_string_length"
            :ocr-match="payment_type.ocr_init_string"
            :currency="props.data?.currnecy_code"
            @ocrRead="hanldeOcrRead"
            :error="fields.attachment.touched ? fields.attachment.error : null"
            @blur="fields.attachment.touched = true"
          />
          <TextInput
            v-model="fields.reference.value"
            v-if="payment_type && payment_type?.require_reference"
            label="Reference"
            :readonly="isReadOnly"
            type="textarea"
            :rows="3"
            :error="fields.reference.touched ? fields.reference.error : null"
          />
        </div>
      </div>
    </div>
  </template>
    <!-- body end -->
    <!-- footer start -->
    <template #footer>
    <DocumentFooter
      :data="formData"
      :show-text="false"
      :readonly="isReadOnly"
      :end-point="endPoint"
      :permissions="permissions"
      :title="title"
      :save-new="false"
      @close="$emit('close')"
      @save="handleSubmit(false)"
      @save-new="handleSubmit(true)"
      @download="handleSubmit(false, true)"
      @mail="handleSubmit(false, false, true)"
      @share="handleSubmit(false, false, false, true)"
      @print="handleSubmit(false, false, false, false, true)"
    />
    </template>
    <!-- footer end -->
  </ModalFormComponent>
</template>
